/**
 * Base API Client
 *
 * Provides a typed, reusable HTTP client for API interactions.
 * Handles error formatting, JSON parsing, and authentication headers.
 */

import { env } from "@/shared/config/env";

/**
 * API error details from server response
 */
export interface ApiErrorDetails {
  error?: string;
  message?: string;
  details?: Record<string, string[]>;
}

/**
 * Custom API error with code and HTTP status
 */
export class ApiError extends Error {
  /** Error code from API or fallback */
  public readonly code: string;
  /** HTTP status code */
  public readonly httpStatus: number;
  /** Additional error details */
  public readonly details?: Record<string, string[]>;

  constructor(
    code: string,
    httpStatus: number,
    message?: string,
    details?: Record<string, string[]>,
  ) {
    super(message || code);
    this.name = "ApiError";
    this.code = code;
    this.httpStatus = httpStatus;
    this.details = details;
  }
}

/**
 * Request configuration options
 */
export interface RequestConfig {
  /** Request headers to merge with defaults */
  headers?: Record<string, string>;
  /** URL query parameters */
  params?: Record<string, any>;
  /** AbortSignal for request cancellation */
  signal?: AbortSignal;
  /** Default error code if server doesn't provide one */
  defaultErrorCode?: string;
}

/**
 * Typed API client class
 *
 * Provides methods for common HTTP operations with proper typing.
 *
 * @example
 * ```ts
 * const client = new ApiClient("/api/v1");
 *
 * // GET request
 * const users = await client.get<User[]>("/users");
 *
 * // POST request with body
 * const newUser = await client.post<User>("/users", { name: "John" });
 *
 * // With auth token
 * client.setAuthToken(token);
 * const profile = await client.get<Profile>("/me");
 * ```
 */
export class ApiClient {
  private baseUrl: string;
  private authToken: string | null = null;
  private defaultHeaders: Record<string, string> = {
    "Content-Type": "application/json",
  };

  /**
   * Creates a new API client instance
   *
   * @param baseUrl - Base URL for all requests (defaults to env.apiUrl)
   */
  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? env.apiBaseUrl;
  }

  /**
   * Sets the authentication token for subsequent requests
   *
   * @param token - JWT token or null to clear
   */
  setAuthToken(token: string | null): void {
    this.authToken = token;
  }

  /**
   * Gets the current auth token
   */
  getAuthToken(): string | null {
    return this.authToken;
  }

  /**
   * Builds request headers including auth if set
   */
  private buildHeaders(config?: RequestConfig): Record<string, string> {
    const headers: Record<string, string> = {
      ...this.defaultHeaders,
      ...config?.headers,
    };

    if (this.authToken) {
      headers["Authorization"] = `Bearer ${this.authToken}`;
    }

    return headers;
  }

  /**
   * Safely parses JSON response
   */
  private async safeJson<T>(response: Response): Promise<T | null> {
    try {
      return (await response.json()) as T;
    } catch {
      return null;
    }
  }

  /**
   * Handles error response and throws ApiError
   */
  private async handleError(
    response: Response,
    defaultCode: string,
  ): Promise<never> {
    const data = await this.safeJson<{ detail?: ApiErrorDetails }>(response);
    const code = data?.detail?.error || defaultCode;
    const message = data?.detail?.message || code;
    const details = data?.detail?.details;

    throw new ApiError(code, response.status, message, details);
  }

  /**
   * Builds a URL with an optional query string
   */
  private buildUrl(path: string, params?: Record<string, any>): string {
    const url = new URL(`${this.baseUrl}${path}`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }
    return url.toString();
  }

  /**
   * Makes an HTTP request
   *
   * @param method - HTTP method
   * @param path - URL path (appended to baseUrl)
   * @param body - Request body (will be JSON stringified)
   * @param config - Additional request configuration
   * @returns Parsed JSON response
   */
  async request<T>(
    method: string,
    path: string,
    body?: unknown,
    config?: RequestConfig,
  ): Promise<T> {
    const url = this.buildUrl(path, config?.params);
    const headers = this.buildHeaders(config);

    const init: RequestInit = {
      method,
      headers,
      signal: config?.signal,
    };

    if (body !== undefined) {
      init.body = JSON.stringify(body);
    }

    const response = await fetch(url, init);

    if (!response.ok) {
      await this.handleError(
        response,
        config?.defaultErrorCode ?? "REQUEST_FAILED",
      );
    }

    const data = await this.safeJson<T>(response);
    if (data === null) {
      throw new ApiError(
        "INVALID_JSON",
        response.status,
        "Invalid JSON response",
      );
    }

    return data;
  }

  /**
   * GET request
   */
  get<T>(
    path: string,
    params?: Record<string, any>,
    config?: RequestConfig,
  ): Promise<T> {
    return this.request<T>("GET", path, undefined, { ...config, params });
  }

  /**
   * POST request
   */
  post<T>(path: string, body?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>("POST", path, body, config);
  }

  /**
   * PUT request
   */
  put<T>(path: string, body?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>("PUT", path, body, config);
  }

  /**
   * PATCH request
   */
  patch<T>(path: string, body?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>("PATCH", path, body, config);
  }

  /**
   * DELETE request
   */
  delete<T>(path: string, config?: RequestConfig): Promise<T> {
    return this.request<T>("DELETE", path, undefined, config);
  }
}

/**
 * Default API client instance
 *
 * Pre-configured singleton for most use cases.
 */
export const apiClient = new ApiClient();
